import React, { useState, useEffect }                                from 'react';
import { Snackbar, Alert, AlertTitle, Box, ThemeProvider, Collapse } from '@mui/material';
import muiThemeNew                                                   from 'config/muiThemeNew';
import { TransitionGroup }                                           from 'react-transition-group';

// Create a custom event for notifications
const ADD_NOTIFICATION_EVENT = 'add-notification';
const CLEAR_NOTIFICATIONS_EVENT = 'clear-notifications';

// Function to trigger notifications from anywhere
export const notify = ({message, options}: {message: string, options?: NotificationEventOptions}) => {
  const event = new CustomEvent<NotificationEvent>(ADD_NOTIFICATION_EVENT, {
    detail: { message, options }
  });
  window.dispatchEvent(event);
};

export const clearNotifications = () => {
  const event = new CustomEvent<NotificationEvent>(CLEAR_NOTIFICATIONS_EVENT);
  window.dispatchEvent(event);
};

// Helper function for each notification type
notify.success = (message: string, options?: NotificationEventOptions) => notify({message, options: {...options, type: 'success'}});
notify.error = (message: string, options?: NotificationEventOptions) => notify({message, options: {...options, type: 'error'}});
notify.info = (message: string, options?: NotificationEventOptions) => notify({message, options: {...options, type: 'info'}});
notify.warning = (message: string, options?: NotificationEventOptions) => notify({message, options: {...options, type: 'warning'}});

export default function AppNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [nextId, setNextId] = useState(0);
  const [timeouts, setTimeouts] = useState<NodeJS.Timeout[]>([]);

  const handleClose = (id: number) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const handleAddNotification = (event: CustomEvent<NotificationEvent>) => {
    const newNotification = {
      message: event.detail.message,
      options: event.detail.options,
      id: nextId
    };

    // Add the new notification to the list
    setNotifications((prev) => {
      const updated = [...prev, newNotification];
      // Keep only the last 5 notifications
      return updated.slice(-5);
    });

    // Handle auto-removal of notifications with duration
    if (newNotification.options?.duration) {
      const timeout = setTimeout(() => {
        handleClose(newNotification.id);
      }, newNotification.options.duration);
      setTimeouts(prev => [...prev, timeout]);
    }

    // Increment the next ID
    setNextId(prev => prev + 1);
  };

  const handleClearNotifications = () => {
    setNotifications([]);
  };

  useEffect(() => {
    window.addEventListener(ADD_NOTIFICATION_EVENT, handleAddNotification as EventListener);
    window.addEventListener(CLEAR_NOTIFICATIONS_EVENT, handleClearNotifications as EventListener);
    return () => {
      window.removeEventListener(ADD_NOTIFICATION_EVENT, handleAddNotification as EventListener);
      window.removeEventListener(CLEAR_NOTIFICATIONS_EVENT, handleClearNotifications as EventListener);
    };
  }, []);

  // Clear timeouts when the component unmounts
  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  return (
    // We have to import the theme here because the Snackbar component doesn't support the theme prop
    <ThemeProvider theme={muiThemeNew}>
      <Snackbar
        open={notifications.length > 0}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          width: '100%',
          maxWidth: '400px'
        }}
      >
        <Box sx={{width: '100%', display: 'flex', flexDirection: 'column'}}>
          <TransitionGroup >
            {notifications.map(notification => (
              <Collapse key={notification.id} >
                <Alert
                  key={notification.id}
                  onClose={() => handleClose(notification.id)}
                  severity={notification.options?.type}
                  variant="standard"
                  sx={{
                    width: '100%',
                    maxWidth: '400px',
                    mb: 1,
                    mt: 1.5
                  }}
                  >
                  {notification.options?.title && <AlertTitle>{notification.options.title}</AlertTitle>}
                  {notification.message}
                </Alert>
              </Collapse>
            ))}
          </TransitionGroup>
        </Box>
      </Snackbar>
    </ThemeProvider>
  );
}

type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface NotificationEvent {
  message: string;
  options?: NotificationEventOptions;
}

interface NotificationEventOptions {
  title?: string;
  type?: NotificationType;
  duration?: number;
}

interface Notification extends NotificationEvent {
  id: number;
}
